/**
 * MicroField — Northstar Stage 3.6 environmental depth.
 *
 * A sparse SVG layer of tiny nodes, micro-arcs, and radial marks
 * living between the portrait halo and R2. Its purpose is to add
 * depth — the eye finds small architectural fragments when it
 * lingers on the central field — without adding brightness or
 * clutter. Everything is at extreme opacity so it never competes
 * with the portrait, name, or emblems.
 *
 * Deterministic geometry: values are computed once from `identity`
 * so the composition never jitters between renders.
 */
import React, { useMemo } from "react";

/** A tiny deterministic PRNG so the same identity size produces the
 *  same MicroField layout on every mount. */
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function MicroField({ identity = 260 }) {
  const photoR = identity / 2;
  const outerR = photoR * 2.8;
  const size = outerR * 2 + 40;
  const cx = size / 2;
  const cy = size / 2;

  const { nodes, microArcs, radialMarks, faintDots } = useMemo(() => {
    const rnd = mulberry32(Math.round(identity * 137));
    const innerR = photoR * 1.15;   // just outside portrait halo
    const bandR = photoR * 2.15;    // just inside R2

    // ~24 sparse nodes at random angles/radii between innerR and bandR.
    const nodes = [];
    for (let i = 0; i < 26; i++) {
      const a = rnd() * Math.PI * 2;
      const r = innerR + rnd() * (bandR - innerR);
      nodes.push({
        x: cx + Math.cos(a) * r,
        y: cy + Math.sin(a) * r,
        s: 0.6 + rnd() * 1.2,
        o: 0.10 + rnd() * 0.18,
      });
    }

    // ~10 micro arcs — very short (~8°), random radii/angles.
    const microArcs = [];
    for (let i = 0; i < 10; i++) {
      const centerAngle = rnd() * 360;
      const span = 6 + rnd() * 12;
      const r = innerR + rnd() * (bandR - innerR);
      microArcs.push({
        r,
        aStart: centerAngle - span / 2,
        aEnd: centerAngle + span / 2,
        o: 0.08 + rnd() * 0.14,
      });
    }

    // ~14 radial tick marks scattered irregularly (not evenly spaced).
    const radialMarks = [];
    for (let i = 0; i < 14; i++) {
      const a = rnd() * Math.PI * 2;
      const rIn = photoR * 1.05 + rnd() * (photoR * 0.55);
      const len = 3 + rnd() * 6;
      radialMarks.push({
        x1: cx + Math.cos(a) * rIn,
        y1: cy + Math.sin(a) * rIn,
        x2: cx + Math.cos(a) * (rIn + len),
        y2: cy + Math.sin(a) * (rIn + len),
        o: 0.10 + rnd() * 0.14,
      });
    }

    // A dust cloud of very faint 0.5 px pixels between R2 and R3.
    const faintDots = [];
    for (let i = 0; i < 60; i++) {
      const a = rnd() * Math.PI * 2;
      const r = photoR * 2.4 + rnd() * (photoR * 0.8);
      faintDots.push({
        x: cx + Math.cos(a) * r,
        y: cy + Math.sin(a) * r,
        o: 0.06 + rnd() * 0.12,
      });
    }
    return { nodes, microArcs, radialMarks, faintDots };
  }, [identity, cx, cy, photoR]);

  return (
    <div
      aria-hidden="true"
      data-testid="northstar-microfield"
      className="absolute left-1/2 top-1/2 pointer-events-none"
      style={{
        transform: "translate(-50%, -50%)",
        width: size,
        height: size,
        zIndex: 3,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {radialMarks.map((m, i) => (
          <line
            key={`m-${i}`}
            x1={m.x1}
            y1={m.y1}
            x2={m.x2}
            y2={m.y2}
            stroke={`rgba(184,239,198,${m.o})`}
            strokeWidth={0.5}
            strokeLinecap="round"
          />
        ))}
        {microArcs.map((a, i) => (
          <path
            key={`a-${i}`}
            d={arcPath(cx, cy, a.r, a.aStart, a.aEnd)}
            fill="none"
            stroke={`rgba(126,212,147,${a.o})`}
            strokeWidth={0.6}
            strokeLinecap="round"
          />
        ))}
        {nodes.map((n, i) => (
          <circle
            key={`n-${i}`}
            cx={n.x}
            cy={n.y}
            r={n.s}
            fill={`rgba(184,239,198,${n.o})`}
          />
        ))}
        {faintDots.map((d, i) => (
          <rect
            key={`d-${i}`}
            x={d.x}
            y={d.y}
            width={0.7}
            height={0.7}
            fill={`rgba(126,212,147,${d.o})`}
          />
        ))}
      </svg>
    </div>
  );
}

function arcPath(cx, cy, r, aDeg, bDeg) {
  const a = (aDeg * Math.PI) / 180;
  const b = (bDeg * Math.PI) / 180;
  const x1 = cx + Math.cos(a) * r;
  const y1 = cy + Math.sin(a) * r;
  const x2 = cx + Math.cos(b) * r;
  const y2 = cy + Math.sin(b) * r;
  const large = bDeg - aDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}
